/**
 * LOT A2 — pure helpers shared by the "Import from booking (AI)" modal and its
 * tests. No React, no network: everything here is deterministic so the risky
 * decisions (who is on the segment, what is sent to the write route) can be
 * proven by a unit test instead of by eye.
 *
 * CONTRACT (mirrors `server/lib/ai-actions.js` `add_flight_leg`):
 *  - the AI extraction is READ-ONLY; nothing exists until one `POST /ai/chat/apply`
 *    per CHECKED segment;
 *  - an empty value is never sent (the server drops empty strings anyway) —
 *    sending "" would make the payload look like a user decision to clear a field;
 *  - a segment is never merged: a duplicate is reported, not overwritten.
 */
import type { AiProposedLeg } from '../api';

/** The minimum a member needs to be matchable against a printed name. */
export interface FlightMemberOption {
  id: string | number;
  name: string;
}

/** What we send as `changes` to `POST /api/ai/chat/apply`. */
export type FlightLegChanges = Record<string, string | string[] | number>;

/** Lowercase, accent-free, punctuation-free tokens — "SMITH, John" and
 * "john smith" must match, "J. Smith" must NOT be trusted (initials are
 * ambiguous: two different people can share them). */
export function normalizePersonName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * True when two printed names denote the same person: same tokens in any order
 * ("SMITH JOHN" = "John Smith"). Every token of the shorter name must appear in
 * the longer one, and at least one token must be longer than 1 character (so a
 * lone initial never matches anybody).
 */
export function namesMatch(a: string, b: string): boolean {
  const ta = normalizePersonName(a).split(' ').filter(Boolean);
  const tb = normalizePersonName(b).split(' ').filter(Boolean);
  if (ta.length === 0 || tb.length === 0) return false;
  if (!ta.some((t) => t.length > 1) || !tb.some((t) => t.length > 1)) return false;
  const [short, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  const longSet = new Set(long);
  return short.every((t) => longSet.has(t));
}

export interface PassengerMatch {
  /** Ids of the team members the document names — never a guessed person. */
  memberIds: string[];
  /** Printed names with no member behind them: shown, never auto-created. */
  unmatched: string[];
}

/**
 * De-duplicate the document's passenger names against the show's members.
 * A name that matches nobody stays UNMATCHED (the user can type it in
 * `person_name`) — the import never creates a team member.
 */
export function matchPassengers(
  passengerNames: string[],
  members: FlightMemberOption[],
): PassengerMatch {
  const ids: string[] = [];
  const unmatched: string[] = [];
  const seen = new Set<string>();
  for (const raw of passengerNames) {
    const name = (raw || '').trim();
    if (!name) continue;
    const key = normalizePersonName(name);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    const hit = members.find((m) => namesMatch(name, m.name));
    if (hit) {
      const id = String(hit.id);
      if (!ids.includes(id)) ids.push(id);
    } else {
      unmatched.push(name);
    }
  }
  return { memberIds: ids, unmatched };
}

/** Stable identity of a proposed segment inside the modal (the list is static
 * once extracted, so the position is a sound key here — unlike a live list). */
export function legKey(leg: AiProposedLeg, index: number): string {
  return [index, leg.mode, leg.flight_date, leg.flight_no, leg.from_iata, leg.to_iata].join('|');
}

/** Human route of a segment: "GVA → MUC", "Genève → Munich", "Genève → Zürich HB". */
export function legRoute(leg: AiProposedLeg): string {
  const left = leg.from_iata || leg.from_city || leg.from_station;
  const right = leg.to_iata || leg.to_city || leg.to_station;
  if (!left && !right) return '';
  return `${left || '—'} → ${right || '—'}`;
}

/** Human "times" of a segment: "08:10 – 09:45" (empty string when none). */
export function legTimes(leg: AiProposedLeg): string {
  if (!leg.departure && !leg.arrival) return '';
  return `${leg.departure || '—'} – ${leg.arrival || '—'}`;
}

/**
 * Build the `changes` payload for ONE confirmed segment.
 * Only non-empty values travel: a missing date in the document must not arrive
 * as "" (the server would treat it as "clear it"), and `mode` is always sent
 * because it is the only required discriminator.
 */
export function legToChanges(
  leg: AiProposedLeg,
  opts: {
    memberIds?: string[];
    personName?: string;
    sortOrder?: number;
  } = {},
): FlightLegChanges {
  const changes: FlightLegChanges = { mode: leg.mode || 'flight' };
  const textFields = [
    'flight_date',
    'airline',
    'flight_no',
    'booking_ref',
    'departure',
    'arrival',
    'from_city',
    'from_iata',
    'from_station',
    'to_city',
    'to_iata',
    'to_station',
    'train_class',
    'coach',
    'seat',
    'platform',
  ] as const;
  for (const field of textFields) {
    const value = (leg[field] || '').trim();
    if (value) changes[field] = value;
  }
  const ids = (opts.memberIds ?? []).map((id) => String(id)).filter(Boolean);
  if (ids.length > 0) changes.member_ids = ids;
  // A free-text passenger is only sent when NO member was matched: otherwise it
  // would duplicate the names already carried by member_ids.
  const personName = (opts.personName || '').trim();
  if (personName && ids.length === 0) changes.person_name = personName;
  if (typeof opts.sortOrder === 'number' && Number.isFinite(opts.sortOrder)) {
    changes.sort_order = opts.sortOrder;
  }
  return changes;
}

/** The apply button is only enabled when at least one segment is ticked. */
export function canImport(checked: ReadonlySet<number>): boolean {
  return checked.size > 0;
}

/** `true` when the apply answer says the segment already existed (nothing written). */
export function isDuplicateSkip(answer: { skipped?: string } | null | undefined): boolean {
  return !!answer && answer.skipped === 'duplicate';
}
